import React from "react";
import { Link } from "react-router-dom";
import { getAuthor, getCategory, MAX_PHOTO } from "../mock";
import { Clock, Eye, Headphones, PlayCircle } from "lucide-react";
import TierRibbon from "./TierRibbon";
import { cropUnsplash } from "../lib/api";
import SmartImage from "./SmartImage";

const AuthorAvatar = ({ size = 20, author }) => (
  <span
    className="inline-block rounded-full overflow-hidden bg-neutral-200 shrink-0 align-middle"
    style={{ width: size, height: size }}
    title={author || "Max Fischer"}
  >
    <img
      src={MAX_PHOTO}
      alt={author || "Max Fischer"}
      className="w-full h-full object-cover"
      style={{ objectPosition: "center 15%" }}
    />
  </span>
);

// Retained for legacy imports; returns null so no date is displayed.
export const timeAgo = () => "";

const CatTag = ({ cat }) => {
  const c = getCategory(cat);
  return (
    <span className="inline-block text-[10px] tracking-[0.25em] uppercase font-semibold" style={{ color: c.color }}>
      {c.label}
    </span>
  );
};

export const ArticleCardLarge = ({ a }) => {
  const author = getAuthor(a.author);
  return (
    <Link to={`/article/${a.slug}`} className="group block">
      <div className="overflow-hidden bg-neutral-200 aspect-[16/10]">
        <SmartImage
          src={cropUnsplash(a.image, { w: 1600, h: 1000 })}
          alt={a.title}
          sizes="(min-width: 1024px) 66vw, 100vw"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
      </div>
      <div className="pt-4">
        <CatTag cat={a.category} />
        <h3 className="mt-2 font-serif-display text-[26px] leading-[1.15] font-bold text-[#0B1F33] group-hover:text-[#0891B2] transition-colors">
          {a.title}
        </h3>
        <p className="mt-2 text-[15px] text-neutral-600 leading-relaxed line-clamp-4">{a.dek}</p>
        <div className="mt-3 flex items-center gap-2 text-[11px] uppercase tracking-wider text-neutral-500">
          <AuthorAvatar size={22} author={author.name} />
          <span>By {author.name}</span>
          {a.audio && <Headphones className="w-3.5 h-3.5 text-[#0891B2]" />}
          {a.video && <PlayCircle className="w-3.5 h-3.5 text-[#0891B2]" />}
        </div>
      </div>
    </Link>
  );
};

export const ArticleCardMedium = ({ a }) => {
  const author = getAuthor(a.author);
  return (
    <Link to={`/article/${a.slug}`} className="group block">
      <div className="overflow-hidden bg-neutral-200 aspect-[16/10]">
        <SmartImage
          src={cropUnsplash(a.image, { w: 1600, h: 1000 })}
          alt={a.title}
          sizes="(min-width: 1024px) 33vw, 50vw"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
      </div>
      <div className="pt-3">
        <CatTag cat={a.category} />
        <h3 className="mt-1.5 font-serif-display text-[19px] leading-[1.2] font-bold text-[#0B1F33] group-hover:text-[#0891B2] transition-colors">
          {a.title}
        </h3>
        <div className="mt-2 flex items-center gap-2 text-[10px] uppercase tracking-wider text-neutral-500">
          <AuthorAvatar size={20} author={author.name} />
          <span>{author.name}</span>
          {a.audio && <Headphones className="w-3 h-3 text-[#0891B2]" />}
          {a.video && <PlayCircle className="w-3 h-3 text-[#0891B2]" />}
          <TierRibbon tier={a.readTier} size="xxs" className="ml-auto" />
        </div>
      </div>
    </Link>
  );
};

export const ArticleCardHorizontal = ({ a }) => {
  const author = getAuthor(a.author);
  return (
    <Link to={`/article/${a.slug}`} className="group grid grid-cols-[130px_1fr] md:grid-cols-[160px_1fr] gap-4 items-start">
      <div className="overflow-hidden bg-neutral-200 aspect-[4/3]">
        <SmartImage
          src={cropUnsplash(a.image, { w: 1600, h: 1000 })}
          alt={a.title}
          sizes="160px"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
      </div>
      <div>
        <CatTag cat={a.category} />
        <h3 className="mt-1 font-serif-display text-[17px] leading-[1.2] font-bold text-[#0B1F33] group-hover:text-[#0891B2] transition-colors line-clamp-3">
          {a.title}
        </h3>
        <div className="mt-2 flex items-center gap-2 text-[10px] uppercase tracking-wider text-neutral-500">
          <AuthorAvatar size={18} author={author.name} />
          <span>{author.name}</span>
          <TierRibbon tier={a.readTier} size="xxs" className="ml-auto" />
        </div>
      </div>
    </Link>
  );
};

export const ArticleCardCompact = ({ a, idx }) => {
  return (
    <Link to={`/article/${a.slug}`} className="group flex gap-3 py-3 border-b border-neutral-200 last:border-0">
      {typeof idx === 'number' && (
        <span className="font-serif-display text-[28px] font-bold text-[#0891B2] leading-none w-8 shrink-0">
          {String(idx + 1).padStart(2, '0')}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <CatTag cat={a.category} />
        <h4 className="mt-1 font-serif-display text-[15px] leading-[1.25] font-semibold text-[#0B1F33] group-hover:text-[#0891B2] transition-colors">
          {a.title}
        </h4>
        <div className="mt-1 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-neutral-500">
          <AuthorAvatar size={14} />
          <span>Max Fischer</span>
          <TierRibbon tier={a.readTier} size="xxs" className="ml-auto" />
        </div>
      </div>
    </Link>
  );
};

// Vexent Wire news-river row: condensed sans headline, plain-text dek,
// compact 140x93 thumb, dense meta with view + audio-play counters.
// When `onListen` is provided AND the article has audio, the plays counter
// is clickable and opens the audio player.
export const NewsRiverRow = ({ a, onListen }) => {
  const dt = a.publishedAt ? new Date(a.publishedAt) : null;
  const timestampStr = a.evergreen
    ? ""
    : dt
    ? dt
        .toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
        })
        .toUpperCase() +
      " · " +
      dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).toUpperCase()
    : "";
  const hasAudio = Boolean(a.audio);
  const plays = a.plays ?? 0;
  const views = a.views ?? 0;
  const isPremium = a.audioTier === "premium";
  return (
    <Link
      to={`/article/${a.slug}`}
      data-testid={`river-row-${a.slug}`}
      className="group grid grid-cols-[100px_1fr] md:grid-cols-[140px_1fr] gap-4 md:gap-5 py-3.5 border-b border-neutral-300 items-start"
    >
      <div className="overflow-hidden bg-neutral-200 w-full aspect-[3/2]">
        <SmartImage
          src={cropUnsplash(a.image, { w: 400, h: 267 })}
          alt={a.title}
          sizes="140px"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="min-w-0">
        {a.specialReport && (
          <div className="mb-1.5">
            <span className="inline-flex items-center gap-1.5 bg-[#7a1c1c] text-[#FFFFFF] px-2 py-0.5 text-[9px] tracking-[0.25em] uppercase font-bold font-condensed">
              <span className="inline-block w-1.5 h-1.5 bg-[#B8E9F1] rounded-full animate-pulse" />
              Special Report
            </span>
          </div>
        )}
        <h3 className="font-condensed text-[18px] md:text-[21px] leading-[1.15] font-bold text-[#0B1F33] group-hover:text-[#0891B2] transition-colors uppercase tracking-[-0.005em]">
          {a.title}
          {a.video && <PlayCircle className="inline w-4 h-4 ml-2 text-[#0891B2] align-baseline" />}
        </h3>
        {a.dek && (
          <p className="mt-1.5 text-[13.5px] md:text-[14px] leading-[1.45] text-neutral-700 line-clamp-2 max-w-[620px]">
            {a.dek}
          </p>
        )}
        <div className="mt-2 flex items-center gap-3 text-[10px] md:text-[11px] tracking-[0.18em] uppercase text-neutral-500 font-mono-tick">
          {timestampStr && <span>{timestampStr}</span>}
          <span className="inline-flex items-center gap-1" title={`${views.toLocaleString()} views`}>
            <Eye className="w-3 h-3 opacity-70" /> {views.toLocaleString()}
          </span>
          {hasAudio && onListen && (
            <button
              type="button"
              data-testid={`listen-btn-${a.slug}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onListen(a);
              }}
              className={`inline-flex items-center gap-1 transition-colors ${isPremium ? "text-[#0891B2] hover:text-[#0891B2]" : "hover:text-[#0891B2]"}`}
              title={isPremium ? "Premium audio — sign up (free) to listen" : "Listen to article"}
              aria-label={isPremium ? "Premium listen" : "Listen"}
            >
              <Headphones className="w-3 h-3" />
              <span>{plays.toLocaleString()}</span>
            </button>
          )}
          {hasAudio && !onListen && (
            <span className={`inline-flex items-center gap-1 ${isPremium ? "text-[#0891B2]" : ""}`}>
              <Headphones className="w-3 h-3 opacity-70" /> {plays.toLocaleString()}
            </span>
          )}
          <TierRibbon tier={a.readTier} size="xxs" className="ml-auto" />
        </div>
      </div>
    </Link>
  );
};
